import { z } from 'zod'
import { accountDisplaySchema } from '../profile/contract.ts'

export const businessTypeSchema = z.enum(['lease', 'valueAdded'])
export const businessStatusSchema = z.enum(['enabled', 'disabled'])

export const BUSINESS_TAG_COLOR_PRESETS = [
  { color: '#34ba9e', label: '青绿色' },
  { color: '#4b5563', label: '石墨灰' },
  { color: '#6366f1', label: '靛蓝色' },
  { color: '#f59e0b', label: '琥珀橙' },
  { color: '#f43f5e', label: '玫红色' },
  { color: '#7c3aed', label: '紫罗兰' },
  { color: '#1d4ed8', label: '深海蓝' },
  { color: '#733e0a', label: '棕褐色' },
  { color: '#0f766e', label: '深青色' },
  { color: '#0284c7', label: '湖蓝色' },
  { color: '#16a34a', label: '翡翠绿' },
  { color: '#65a30d', label: '青柠绿' },
  { color: '#d97706', label: '金黄色' },
  { color: '#c2410c', label: '活力橙' },
  { color: '#be123c', label: '深玫红' },
  { color: '#a21caf', label: '品红色' }
] as const

export const BUSINESS_DEFAULT_TAG_COLOR = BUSINESS_TAG_COLOR_PRESETS[0].color

export const businessColorSchema = z
  .string()
  .trim()
  .regex(/^#[\da-f]{6}$/i, '标签颜色格式不正确')

export const businessListSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(255).optional(),
  type: businessTypeSchema.optional(),
  status: businessStatusSchema.optional()
})

const businessPriceSchema = z.number().min(0).max(9_999_999_999.99).multipleOf(0.01, '金额最多保留两位小数')

// 业务参考价格统一存入 JSONB；null / 缺省代表未配置，0 是有效配置。
export const businessPricingSchema = z.object({
  referencePrice: businessPriceSchema.nullish(),
  costPrice: businessPriceSchema.nullish(),
  waterUnitPrice: businessPriceSchema.nullish(),
  electricityUnitPrice: businessPriceSchema.nullish(),
  depositAmount: businessPriceSchema.nullish(),
  managementFee: businessPriceSchema.nullish()
})

export const OPTIONAL_RENTAL_FEE_NOTICE = '押金、水费、电费和管理费留空不生成对应账单；填写后（包括 0）会生成对应账单。'

export const businessUpsertSchema = z.object({
  name: z.string().trim().min(1, '业务名称不能为空').max(255),
  type: businessTypeSchema,
  defaultServiceMode: z.enum(['once', 'term']).default('once'),
  color: businessColorSchema.default(BUSINESS_DEFAULT_TAG_COLOR),
  ...businessPricingSchema.shape,
  note: z.string().trim().max(5000).optional().nullable()
})

export const businessUpsertRequestSchema = z.object({
  id: z.number().int().positive().optional(),
  data: businessUpsertSchema
})

export const businessRemoveSchema = z.object({
  id: z.number().int().positive()
})

export const businessSetEnabledSchema = z.object({
  id: z.number().int().positive(),
  isEnabled: z.boolean()
})

const businessIdsSchema = z
  .array(z.number().int().positive())
  .min(1, '至少选择一个业务')
  .max(100, '单次最多处理 100 个业务')

export const businessBatchSetEnabledSchema = z.object({
  ids: businessIdsSchema,
  isEnabled: z.boolean()
})

export const businessBatchRemoveSchema = z.object({ ids: businessIdsSchema })

export const businessBatchOperationResultSchema = z.object({ count: z.number().int().nonnegative() })

export const businessSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  type: businessTypeSchema,
  defaultServiceMode: z.enum(['once', 'term']).default('once'),
  color: businessColorSchema,
  isEnabled: z.boolean(),
  referencePrice: z.string().nullable(),
  costPrice: z.string().nullable(),
  waterUnitPrice: z.string().nullable(),
  electricityUnitPrice: z.string().nullable(),
  depositAmount: z.string().nullable(),
  managementFee: z.string().nullable(),
  note: z.string().nullable(),
  creator: accountDisplaySchema,
  createdAt: z.date(),
  updatedAt: z.date()
})

export const businessListOutputSchema = z.object({
  data: z.array(businessSchema.extend({ orderCount: z.number().int().nonnegative() })),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})
export const businessOptionsInputSchema = z.object({
  keyword: z.string().trim().max(255).optional(),
  type: businessTypeSchema.optional()
})
export const businessOptionSchema = z.object({
  label: z.string(),
  value: z.number().int().positive(),
  type: businessTypeSchema,
  defaultServiceMode: z.enum(['once', 'term']).default('once'),
  referencePrice: z.string().nullable(),
  waterUnitPrice: z.string().nullable(),
  electricityUnitPrice: z.string().nullable(),
  depositAmount: z.string().nullable(),
  managementFee: z.string().nullable()
})

export type BusinessType = z.infer<typeof businessTypeSchema>
export type BusinessStatus = z.infer<typeof businessStatusSchema>
export type BusinessListInput = z.infer<typeof businessListSchema>
export type BusinessOptionsInput = z.infer<typeof businessOptionsInputSchema>
export type BusinessUpsertInput = z.infer<typeof businessUpsertSchema>
export type BusinessSetEnabledInput = z.infer<typeof businessSetEnabledSchema>
export type BusinessBatchSetEnabledInput = z.infer<typeof businessBatchSetEnabledSchema>
export type BusinessRemoveInput = z.infer<typeof businessRemoveSchema>
export type BusinessBatchRemoveInput = z.infer<typeof businessBatchRemoveSchema>

export const businessBatchCreateSchema = z.object({
  type: businessTypeSchema,
  names: z.array(z.string().trim()).min(1, '请输入业务名称').max(100, '单次最多添加 100 个业务')
})

export const businessBatchCreateResultSchema = z.object({
  successCount: z.number().int().nonnegative(),
  failureCount: z.number().int().nonnegative(),
  failures: z.array(z.object({ name: z.string(), reason: z.string() }))
})

export type BusinessBatchCreateInput = z.infer<typeof businessBatchCreateSchema>
export type BusinessBatchCreateResult = z.infer<typeof businessBatchCreateResultSchema>

export type BusinessPricing = z.infer<typeof businessPricingSchema>
export type BusinessOption = z.infer<typeof businessOptionSchema>
